import React from "react";

const updatedDate = "May 16, 2026";

const styles = {
  page: {
    minHeight: "100vh",
    background:
      "radial-gradient(circle at top left, rgba(197,155,69,0.12), transparent 32%), radial-gradient(circle at top right, rgba(5,7,19,0.10), transparent 30%), linear-gradient(to bottom, #F7F8FA, #F2F3F5 45%, #F7F8FA)",
    color: "#0F172A",
    padding: "72px 20px",
    fontFamily:
      "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif",
  },
  container: {
    maxWidth: "1040px",
    margin: "0 auto",
    lineHeight: 1.75,
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 12px",
    borderRadius: "999px",
    background: "#FFF9EA",
    color: "#7A5A17",
    border: "1px solid #E7D8AC",
    fontSize: "12px",
    fontWeight: 700,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    marginBottom: "18px",
  },
  h1: {
    fontSize: "clamp(32px, 4vw, 48px)",
    lineHeight: 1.08,
    margin: "0 0 14px",
    letterSpacing: "-0.04em",
    color: "#050713",
    fontWeight: 700,
  },
  updated: {
    color: "#64748B",
    marginBottom: "34px",
    fontSize: "14px",
  },
  h2: {
    fontSize: "18px",
    marginTop: "30px",
    marginBottom: "10px",
    color: "#050713",
    letterSpacing: "-0.015em",
    fontWeight: 700,
  },
  p: {
    color: "#475569",
    margin: "0 0 14px",
    fontSize: "15px",
  },
  ul: {
    color: "#475569",
    paddingLeft: "22px",
    marginTop: "8px",
    fontSize: "15px",
  },
  box: {
    border: "1px solid #E2E8F0",
    background: "#FFFFFF",
    borderRadius: "24px",
    padding: "24px",
    margin: "24px 0 32px",
    boxShadow: "0 18px 55px rgba(15, 23, 42, 0.06)",
  },
  document: {
    border: "1px solid #E2E8F0",
    background: "#FFFFFF",
    borderRadius: "30px",
    padding: "32px",
    boxShadow: "0 18px 55px rgba(15, 23, 42, 0.06)",
  },
  link: {
    color: "#7A5A17",
    textDecoration: "none",
    fontWeight: 700,
  },
};

export default function Privacy() {
  return (
    <main style={styles.page}>
      <section style={styles.container}>
        <div style={styles.badge}>Krionex Legal</div>

        <h1 style={styles.h1}>Privacy Policy</h1>
        <p style={styles.updated}>Last updated: {updatedDate}</p>

        <div style={styles.box}>
          <p style={styles.p}>
            This Privacy Policy explains how Krionex collects, uses, stores, and
            protects information when you access our website, connect a wallet,
            use our application, deploy smart contracts, mint assets, manage
            deployment history, purchase subscriptions, or contact support.
          </p>
          <p style={styles.p}>
            By using Krionex, you acknowledge this Privacy Policy.
          </p>
        </div>
        <div style={styles.document}>
          <h2 style={styles.h2}>1. Information We Collect</h2>
          <p style={styles.p}>
            Depending on how you use Krionex, we may collect the following
            categories of information:
          </p>
          <ul style={styles.ul}>
            <li>
              <strong>Account information:</strong> such as email address,
              username, authentication details, and workspace settings.
            </li>
            <li>
              <strong>Wallet information:</strong> such as wallet address,
              connected network, chain ID, wallet provider, and wallet connection
              status.
            </li>
            <li>
              <strong>Deployment information:</strong> such as contract addresses,
              transaction hashes, deployment timestamps, selected blockchain
              networks, token configuration, minting records, and deployment
              history.
            </li>
            <li>
              <strong>Payment information:</strong> such as plan type,
              subscription status, billing email, payment processor customer ID,
              invoice status, and payment history. Full card details are handled
              by our payment processor and are not stored by Krionex.
            </li>
            <li>
              <strong>Technical information:</strong> such as IP address, browser
              type, device information, operating system, referring pages, log
              data, crash reports, and usage events.
            </li>
            <li>
              <strong>Support information:</strong> such as messages, emails,
              issue reports, feedback, and attachments you send to us.
            </li>
          </ul>

          <h2 style={styles.h2}>2. Blockchain Data</h2>
          <p style={styles.p}>
            Blockchain networks are public or semi-public systems. When you deploy
            a contract, mint an asset, sign a transaction, transfer tokens, or
            interact with a blockchain, information such as wallet addresses,
            contract addresses, transaction hashes, token metadata, timestamps,
            balances, and event logs may become publicly visible.
          </p>
          <p style={styles.p}>
            Krionex cannot delete, hide, reverse, modify, anonymize, or remove
            data that has already been recorded on a blockchain or indexed by
            third-party services such as block explorers, RPC providers, analytics
            platforms, or blockchain indexers.
          </p>

          <h2 style={styles.h2}>3. Deployment Privacy Controls</h2>
          <p style={styles.p}>
            Krionex may provide privacy controls that allow you to hide deployment
            records, contract addresses, transaction hashes, or related activity
            from your Krionex workspace or Krionex-controlled interfaces.
          </p>
          <p style={styles.p}>
            These controls affect visibility inside Krionex only. They do not
            erase, remove, or hide records from public blockchain networks,
            third-party explorers, wallets, RPC providers, indexers, analytics
            tools, or other external systems.
          </p>

          <h2 style={styles.h2}>4. How We Use Information</h2>
          <p style={styles.p}>We may use information to:</p>
          <ul style={styles.ul}>
            <li>provide, operate, secure, and improve Krionex;</li>
            <li>enable wallet connection, authentication, and user sessions;</li>
            <li>create, display, organize, and manage deployment history;</li>
            <li>support token deployment, minting, and smart contract workflows;</li>
            <li>process subscriptions, billing, invoices, and payment status;</li>
            <li>detect abuse, fraud, security threats, and prohibited activity;</li>
            <li>debug errors, monitor performance, and improve reliability;</li>
            <li>respond to support requests and user communications;</li>
            <li>comply with legal, tax, accounting, security, and regulatory obligations.</li>
          </ul>

          <h2 style={styles.h2}>5. Payments</h2>
          <p style={styles.p}>
            Payments may be processed by third-party payment providers. Krionex
            does not store full credit card numbers or complete payment card
            details on its own servers. Payment providers may collect and process
            payment information according to their own privacy policies and terms.
          </p>
          <p style={styles.p}>
            We may receive limited billing-related information such as payment
            status, subscription plan, customer ID, invoice status, billing email,
            and transaction reference.
          </p>

          <h2 style={styles.h2}>6. Analytics and Logs</h2>
          <p style={styles.p}>
            Krionex may collect usage analytics, diagnostic logs, performance
            metrics, error reports, and security logs to understand how the
            platform is used, improve product experience, detect technical issues,
            prevent abuse, and protect the platform.
          </p>
          <p style={styles.p}>
            Where practical, we limit access to logs and retain them only as long
            as reasonably needed for operational, security, legal, or business
            purposes.
          </p>

          <h2 style={styles.h2}>7. Cookies and Similar Technologies</h2>
          <p style={styles.p}>
            Krionex may use cookies, local storage, session storage, and similar
            technologies to keep users signed in, remember preferences, support
            wallet connection flows, measure usage, improve performance, and
            secure the platform.
          </p>
          <p style={styles.p}>
            You may control cookies through your browser settings, but disabling
            certain technologies may affect platform functionality.
          </p>

          <h2 style={styles.h2}>8. How We Share Information</h2>
          <p style={styles.p}>
            We do not sell your personal information. We may share information
            with trusted service providers and infrastructure partners that help us
            operate Krionex, including:
          </p>
          <ul style={styles.ul}>
            <li>cloud hosting and infrastructure providers;</li>
            <li>database, logging, and monitoring providers;</li>
            <li>payment processors and billing providers;</li>
            <li>analytics and product improvement tools;</li>
            <li>customer support and communication tools;</li>
            <li>security, fraud prevention, and abuse detection providers;</li>
            <li>legal, compliance, accounting, or professional advisors where necessary.</li>
          </ul>
          <p style={styles.p}>
            We may also disclose information if required by law, court order,
            regulatory request, legal process, security investigation, or to
            protect the rights, safety, and security of Krionex, our users, or
            others.
          </p>

          <h2 style={styles.h2}>9. Security</h2>
          <p style={styles.p}>
            We use reasonable technical and organizational measures designed to
            protect Krionex and the information we process. These may include
            access controls, encryption in transit, infrastructure monitoring,
            logging, environment separation, restricted administrative access, and
            security review practices.
          </p>
          <p style={styles.p}>
            However, no system is completely secure. Krionex cannot guarantee that
            unauthorized access, data loss, wallet compromise, third-party service
            failure, or security incidents will never occur.
          </p>
          <p style={styles.p}>
            You are responsible for securing your wallet, private keys, seed
            phrases, devices, browser, email account, passwords, and authentication
            methods.
          </p>

          <h2 style={styles.h2}>10. Data Retention</h2>
          <p style={styles.p}>
            We retain information for as long as reasonably necessary to provide
            Krionex, maintain security, resolve disputes, comply with legal
            obligations, enforce our terms, process billing, and improve the
            platform.
          </p>
          <p style={styles.p}>
            Some information may remain in backups, logs, audit records, billing
            records, or security systems for a limited period. Public blockchain
            data may remain permanently available independently of Krionex.
          </p>

          <h2 style={styles.h2}>11. Your Choices and Rights</h2>
          <p style={styles.p}>
            Depending on your location, you may have rights to access, correct,
            delete, export, restrict, or object to certain processing of your
            personal information.
          </p>
          <p style={styles.p}>
            You may contact us to request assistance with your data. We may need
            to verify your identity before responding. Some requests may be limited
            where data is required for security, legal compliance, billing,
            dispute resolution, or where the data exists on public blockchain
            networks outside Krionex’s control.
          </p>

          <h2 style={styles.h2}>12. International Users</h2>
          <p style={styles.p}>
            Krionex may be accessed by users in different countries. Your
            information may be processed in countries other than where you live.
            By using Krionex, you understand that information may be transferred,
            stored, and processed internationally where our service providers,
            infrastructure, or operations are located.
          </p>

          <h2 style={styles.h2}>13. Children</h2>
          <p style={styles.p}>
            Krionex is not intended for children. You must be legally old enough
            to use Krionex and enter into these terms in your jurisdiction. We do
            not knowingly collect personal information from children.
          </p>

          <h2 style={styles.h2}>14. Changes to This Policy</h2>
          <p style={styles.p}>
            We may update this Privacy Policy from time to time. The updated
            version will be posted on this page with a revised “Last updated”
            date. Your continued use of Krionex after changes become effective
            means you acknowledge the updated policy.
          </p>

          <h2 style={styles.h2}>15. Contact</h2>
          <p style={styles.p}>
            For questions about this Privacy Policy or your information, contact
            us at{" "}
            <a style={styles.link} href="mailto:support@krionex.com">
              support@krionex.com
            </a>
            .
          </p>
        </div>
      </section>
    </main>
  );
}